import React, { ChangeEvent } from "react";
import {
  createStyles,
  withStyles,
  type WithStyles,
} from "@material-ui/core/styles";
import { TextField } from "@material-ui/core";
import Tooltip from "@mui/material/Tooltip";
import InfoIcon from "@mui/icons-material/Info";
import { UserFormState } from "../userForm.types";
import { useTranslations } from "adapters/i18n";

type UserDataFormProps = {
  isEditing: boolean;
  formState: UserFormState;
  onFieldBlur: (field: string) => Promise<void>;
  onChangeField: <T extends keyof UserFormState>(
    field: T,
    value: UserFormState[T],
  ) => void;
} & WithStyles<typeof styles>;

function UserDataForm({
  classes,
  isEditing,
  formState,
  onFieldBlur,
  onChangeField,
}: UserDataFormProps) {
  const { t } = useTranslations();

  const permissionCheckboxes = [
    {
      label: t("user:permissions.can-access-platform"),
      field: "canAccessPortal",
    },
    { label: t("common:application"), field: "canAccessApp" },
    { label: t("user:permissions.final-stock"), field: "finalStock" },
    { label: t("user:permissions.import-maps"), field: "importMaps" },
    {
      label: t("user:permissions.import-export-pasture-csv"),
      field: "exportAndImportPastureCSV",
    },
    {
      label: t("user:permissions.register-forage-cycle"),
      field: "registerForageCycle",
    },
    {
      label: t("user:permissions.access-benchmark"),
      field: "canAccessBenchmark",
    },
  ] satisfies { label: string; field: keyof UserFormState }[];

  const handleChangeTextField = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    onChangeField(name as keyof UserFormState, { value, error: null });
  };

  const handleChangeCheckbox = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    onChangeField(name as keyof UserFormState, { value: checked });
  };

  const handleBlur = (event: ChangeEvent) => {
    const { name } = event.target as HTMLInputElement;
    onFieldBlur(name);
  };

  return (
    <div className={classes.container}>
      <div className={classes.inputContainer}>
        <div>
          <TextField
            className={classes.textField}
            error={!!formState.name.error}
            InputLabelProps={{ shrink: true }}
            label={t("user:full-name")}
            name={"name" satisfies keyof UserFormState}
            onChange={handleChangeTextField}
            placeholder={t("user:user-full-name")}
            required={true}
            value={formState.name.value}
          />
          <ErrorMessage classes={classes} message={formState.name.error} />
        </div>
        <div>
          <TextField
            className={classes.textField}
            disabled={isEditing}
            error={!!formState.login.error}
            InputLabelProps={{ shrink: true }}
            label={<LoginLabel classes={classes} />}
            name={"login" satisfies keyof UserFormState}
            onBlur={handleBlur}
            onChange={handleChangeTextField}
            placeholder={t("user:login-example")}
            value={formState.login.value}
          />
          <ErrorMessage classes={classes} message={formState.login.error} />
        </div>
        <div>
          <TextField
            className={classes.textField}
            error={!!formState.email.error}
            InputLabelProps={{ shrink: true }}
            label={t("common:fields.email")}
            name={"email" satisfies keyof UserFormState}
            onBlur={handleBlur}
            onChange={handleChangeTextField}
            placeholder={t("user:enter-user-email")}
            required={true}
            value={formState.email.value}
          />
          <ErrorMessage classes={classes} message={formState.email.error} />
        </div>
      </div>
      <div className={classes.checkboxContainer}>
        <div className={classes.checkboxHeader}>
          <Tooltip
            arrow={true}
            placement="right-end"
            title={t("user:allow-access-disclaimer")}
          >
            <InfoIcon className={classes.disclaimerIcon} />
          </Tooltip>
          <span>{`${t("user:allow-access-to")}:`}</span>
        </div>
        <div className={classes.checkboxContent}>
          {permissionCheckboxes.map(({ field, label }) => (
            <Checkbox
              isChecked={formState[field].value}
              classes={classes}
              key={field}
              label={label}
              name={field}
              onChange={handleChangeCheckbox}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

type ErrorMessageProps = {
  message: string | null;
} & WithStyles<typeof styles>;

function ErrorMessage({ classes, message }: ErrorMessageProps) {
  if (message == null) {
    return null;
  }

  return (
    <div data-testid="field-error" className={classes.error}>
      <span>{message}</span>
    </div>
  );
}

type LoginLabelProps = WithStyles<typeof styles>;

function LoginLabel({ classes }: LoginLabelProps) {
  const { t } = useTranslations();

  return (
    <div className={classes.loginLabel}>
      <Tooltip
        arrow={true}
        placement="left-end"
        style={{ marginRight: 4 }}
        title={t("user:login-disclaimer")}
      >
        <InfoIcon className={classes.disclaimerIcon} />
      </Tooltip>
      <span>{`${t("user:user-login")} *`}</span>
    </div>
  );
}

type CheckboxProps = {
  isChecked: boolean;
  label: string;
  name: keyof UserFormState;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
} & WithStyles<typeof styles>;

function Checkbox({
  classes,
  isChecked,
  label,
  name,
  onChange,
}: CheckboxProps) {
  return (
    <div className={classes.checkbox}>
      <input
        className={classes.checkboxInput}
        type="checkbox"
        name={name}
        checked={!!isChecked}
        onChange={onChange}
      />
      <label htmlFor={name}>{label}</label>
    </div>
  );
}

const styles = () =>
  createStyles({
    container: {
      marginTop: "24px",
      display: "flex",
      flexDirection: "column",
      gap: "32px",
    },
    inputContainer: {
      display: "flex",
      gap: "32px",
      flexWrap: "wrap",
    },
    textField: {
      width: "300px",
    },
    error: {
      maxWidth: "300px",
      "& span": {
        color: "red",
        fontFamily: "Roboto",
        fontSize: "14px",
      },
    },
    loginLabel: {
      display: "flex",
      alignItems: "center",
    },
    disclaimerIcon: {
      color: "#616161",
      fontSize: "16px !important",
    },
    checkboxContainer: {
      display: "flex",
      gap: "24px",
      flexDirection: "column",
    },
    checkboxHeader: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      fontFamily: "Roboto",
    },
    checkboxContent: {
      display: "flex",
      alignItems: "center",
      gap: "24px",
      fontSize: "16px",
      flexWrap: "wrap",
    },
    checkbox: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      fontFamily: "Roboto",
    },
    checkboxInput: {
      width: "20px",
      height: "20px",
      margin: 0,
      cursor: "pointer",
    },
  });

export default withStyles(styles)(UserDataForm);
